import {
  Code,
  BarChart3,
  Share2,
  Globe,
  Search,
  Target,
  Megaphone,
  TrendingUp,
  Users,
  Shield,
  Zap,
  HeartHandshake,
  Award,
  Clock,
  Headphones,
  Video,
  Palette,
  PenTool,
  FileText,
  Youtube,
} from "lucide-react";

export const services = [
  {
    id: 1,
    icon: Code,
    title: "Web Development",
    shortDesc: "Custom websites that convert visitors into customers.",
    fullDesc:
      "We build blazing-fast, responsive websites tailored to your brand. From business websites to complex e-commerce platforms and high-converting landing pages.",
    features: [
      "Business Websites",
      "E-commerce Solutions",
      "Landing Pages",
      "Custom Web Apps",
      "CMS Development",
      "API Integrations",
    ],
  },
  {
    id: 2,
    icon: BarChart3,
    title: "Digital Marketing",
    shortDesc: "Data-driven campaigns that maximize your ROI.",
    fullDesc:
      "Strategic digital marketing campaigns across Meta Ads, Google Ads, and SEO to drive qualified traffic and boost your online presence.",
    features: [
      "Meta Ads (Facebook & Instagram)",
      "Google Ads (Search & Display)",
      "SEO Optimization",
      "Content Marketing",
      "Email Marketing",
      "Analytics & Reporting",
    ],
  },
  {
    id: 3,
    icon: Share2,
    title: "Social Media Management",
    shortDesc: "Build a loyal community around your brand.",
    fullDesc:
      "Complete social media management including content creation, community engagement, analytics tracking, and strategic growth planning.",
    features: [
      "Content Strategy",
      "Post Design & Scheduling",
      "Community Management",
      "Influencer Outreach",
      "Analytics & Insights",
      "Brand Voice Development",
    ],
  },
  {
    id: 4,
    icon: Video,
    title: "Video Editing",
    shortDesc: "Professional video editing that makes your brand stand out.",
    fullDesc:
      "We create high-quality edited videos for social media, ads, promos, YouTube, and business branding. From cuts and transitions to motion graphics and storytelling, we turn raw footage into compelling visual content.",
    features: [
      "Reels & Shorts Editing",
      "Promo Videos",
      "YouTube Video Editing",
      "Motion Graphics",
      "Color Correction",
      "Sound Sync & Effects",
    ],
  },
  {
    id: 5,
    icon: Palette,
    title: "Graphics Design",
    shortDesc: "Creative visuals that strengthen your brand identity.",
    fullDesc:
      "Our graphic design services help businesses communicate visually with modern, eye-catching, and professional designs for digital and print platforms.",
    features: [
      "Social Media Creatives",
      "Logo Design",
      "Brand Identity",
      "Marketing Materials",
      "Banner Design",
      "Ad Creatives",
    ],
  },
  {
    id: 6,
    icon: FileText,
    title: "Content Writing",
    shortDesc: "Engaging content that informs, ranks, and converts.",
    fullDesc:
      "We write compelling, SEO-friendly content that connects with your audience and supports your business goals, from web copy and blogs to marketing content and brand messaging.",
    features: [
      "Website Copywriting",
      "Blog Writing",
      "SEO Content",
      "Product Descriptions",
      "Ad Copy",
      "Brand Messaging",
    ],
  },
];

export const serviceDetails = [
  {
    id: "web-development",
    icon: Code,
    title: "Web Development",
    tagline: "Pixel-perfect websites that drive results",
    description:
      "Our web development team crafts stunning, high-performance websites that not only look incredible but also convert visitors into loyal customers. We use the latest technologies and best practices to ensure your online presence stands out.",
    highlights: [
      {
        icon: Globe,
        label: "Business Websites",
        desc: "Professional sites that establish credibility and attract clients.",
      },
      {
        icon: Target,
        label: "E-commerce Solutions",
        desc: "Feature-rich online stores optimized for maximum conversions.",
      },
      {
        icon: Zap,
        label: "Landing Pages",
        desc: "High-converting pages designed for specific campaigns and goals.",
      },
    ],
  },
  {
    id: "digital-marketing",
    icon: BarChart3,
    title: "Digital Marketing",
    tagline: "Strategies that deliver measurable growth",
    description:
      "We develop data-driven marketing strategies that put your brand in front of the right audience at the right time. Our campaigns are designed to maximize ROI and deliver sustainable growth.",
    highlights: [
      {
        icon: Megaphone,
        label: "Meta Ads",
        desc: "Targeted campaigns on Facebook & Instagram that reach your ideal customers.",
      },
      {
        icon: Search,
        label: "Google Ads",
        desc: "Search and display advertising that captures high-intent traffic.",
      },
      {
        icon: TrendingUp,
        label: "SEO Optimization",
        desc: "Organic search strategies that build long-term visibility.",
      },
    ],
  },
  {
    id: "social-media",
    icon: Share2,
    title: "Social Media Management",
    tagline: "Building communities that champion your brand",
    description:
      "We manage your social media presence end-to-end, creating engaging content, fostering community interaction, and building a loyal following that advocates for your brand.",
    highlights: [
      {
        icon: Users,
        label: "Community Building",
        desc: "Engage and grow a loyal audience around your brand story.",
      },
      {
        icon: Target,
        label: "Content Strategy",
        desc: "Tailored content calendars that align with your business goals.",
      },
      {
        icon: BarChart3,
        label: "Performance Analytics",
        desc: "Data-driven insights to continuously optimize your social presence.",
      },
    ],
  },
  {
    id: "video-editing",
    icon: Video,
    title: "Video Editing",
    tagline: "High-impact videos that capture attention",
    description:
      "Our video editing service helps brands create polished and engaging visual content for social media, advertising campaigns, websites, and YouTube. We focus on storytelling, pacing, visual effects, and quality to maximize audience retention and engagement.",
    highlights: [
      {
        icon: Video,
        label: "Reels & Shorts",
        desc: "Fast-paced short-form videos optimized for Instagram, TikTok, and YouTube Shorts.",
      },
      {
        icon: Zap,
        label: "Promo Videos",
        desc: "Professional promotional edits designed to boost conversions and brand visibility.",
      },
      {
        icon: TrendingUp,
        label: "Motion Graphics",
        desc: "Dynamic text, transitions, and effects that make your content more engaging.",
      },
    ],
  },
  {
    id: "graphics-design",
    icon: Palette,
    title: "Graphics Design",
    tagline: "Creative visuals that elevate your brand",
    description:
      "We design visual assets that help businesses stand out in a crowded market. From logos and branding to ad creatives and social media posts, our designs are tailored to communicate your message clearly and professionally.",
    highlights: [
      {
        icon: Palette,
        label: "Brand Identity",
        desc: "Logos, colors, and visual systems that define your brand personality.",
      },
      {
        icon: Megaphone,
        label: "Ad Creatives",
        desc: "High-converting banners and promotional designs for paid campaigns.",
      },
      {
        icon: Share2,
        label: "Social Media Design",
        desc: "Scroll-stopping creatives built for engagement across all major platforms.",
      },
    ],
  },
  {
    id: "content-writing",
    icon: FileText,
    title: "Content Writing",
    tagline: "Words that connect, convert, and rank",
    description:
      "Our content writing services help brands communicate clearly and effectively. We create persuasive, SEO-optimized, and audience-focused content for websites, blogs, ads, and digital campaigns.",
    highlights: [
      {
        icon: FileText,
        label: "Website Copy",
        desc: "Clear and persuasive web content that turns visitors into customers.",
      },
      {
        icon: PenTool,
        label: "Blog Writing",
        desc: "Valuable blog articles that improve authority, traffic, and engagement.",
      },
      {
        icon: Search,
        label: "SEO Content",
        desc: "Search-optimized writing designed to improve rankings and attract organic traffic.",
      },
    ],
  },
];

export const pricingPlans = [
  {
    name: "Basic",
    price: "499",
    period: "/month",
    description: "Perfect for startups and small businesses getting started.",
    features: [
      "Single Page Website",
      "Basic SEO Setup",
      "3 Social Media Posts/Week",
      "Monthly Performance Report",
      "Email Support",
    ],
    highlighted: false,
  },
  {
    name: "Standard",
    price: "999",
    period: "/month",
    description: "Ideal for growing businesses ready to scale their presence.",
    features: [
      "Multi-Page Website",
      "Advanced SEO & Google Ads",
      "5 Social Media Posts/Week",
      "Meta Ads Campaign Management",
      "Bi-Weekly Strategy Calls",
      "Priority Support",
    ],
    highlighted: true,
  },
  {
    name: "Premium",
    price: "1,999",
    period: "/month",
    description: "For established brands seeking comprehensive digital growth.",
    features: [
      "Custom Web Application",
      "Full SEO + Google Ads + Meta Ads",
      "Daily Social Media Management",
      "Content Creation & Copywriting",
      "Weekly Strategy Sessions",
      "Dedicated Account Manager",
      "24/7 Priority Support",
    ],
    highlighted: false,
  },
];

export const portfolioProjects = [
  {
    id: 1,
    title: "TechVault E-commerce",
    category: "Web Development",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
    description:
      "A full-featured e-commerce platform with seamless checkout, real-time inventory, and personalized recommendations.",
    results: [
      "150% increase in online sales",
      "40% reduction in cart abandonment",
      "2.5s average page load time",
    ],
    tags: ["React", "Node.js", "Stripe"],
  },
  {
    id: 2,
    title: "GreenLife Organic",
    category: "Digital Marketing",
    image:
      "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=600&h=400&fit=crop",
    description:
      "Comprehensive digital marketing campaign for an organic food brand, driving brand awareness and conversions.",
    results: [
      "300% ROI on ad spend",
      "50K new followers in 3 months",
      "200% increase in web traffic",
    ],
    tags: ["Meta Ads", "Google Ads", "SEO"],
  },
  {
    id: 3,
    title: "FitPro Gym",
    category: "Social Media",
    image:
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop",
    description:
      "Complete social media overhaul for a fitness chain, building an engaged community of health enthusiasts.",
    results: [
      "10x engagement rate increase",
      "25K organic followers",
      "80% member retention improvement",
    ],
    tags: ["Instagram", "TikTok", "Content"],
  },
  {
    id: 4,
    title: "LuxeStay Hotels",
    category: "Web Development",
    image:
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=400&fit=crop",
    description:
      "Elegant booking platform with virtual tours, dynamic pricing, and multi-language support for a luxury hotel chain.",
    results: [
      "90% increase in direct bookings",
      "35% higher average booking value",
      "4.9/5 user satisfaction score",
    ],
    tags: ["Next.js", "Booking API", "i18n"],
  },
  {
    id: 5,
    title: "FinTrack Pro",
    category: "Web Development",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
    description:
      "SaaS dashboard for financial analytics with real-time data visualization and automated reporting.",
    results: [
      "500+ active enterprise users",
      "60% reduction in report generation time",
      "99.9% uptime",
    ],
    tags: ["React", "D3.js", "AWS"],
  },
  {
    id: 6,
    title: "Bloom Florals",
    category: "Social Media",
    image:
      "https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&h=400&fit=crop",
    description:
      "Social media strategy and content creation for a premium florist, driving both online orders and foot traffic.",
    results: [
      "400% increase in online orders",
      "15K Instagram followers in 6 months",
      "60% increase in store visits",
    ],
    tags: ["Instagram", "Pinterest", "Content"],
  },
  {
    id: 7,
    title: "AutoDrive Dealership",
    category: "Digital Marketing",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
    description:
      "Lead generation campaign for an automotive dealership using targeted PPC and retargeting strategies.",
    results: [
      "250% increase in qualified leads",
      "45% lower cost per acquisition",
      "120 vehicles sold via digital",
    ],
    tags: ["Google Ads", "Retargeting", "CRM"],
  },
  {
    id: 8,
    title: "EduSpark Academy",
    category: "Digital Marketing",
    image:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=600&h=400&fit=crop",
    description:
      "Full-funnel marketing strategy for an online education platform, from awareness to enrollment.",
    results: [
      "5,000+ new enrollments",
      "180% increase in organic traffic",
      "35% conversion rate on landing pages",
    ],
    tags: ["SEO", "Content Marketing", "Email"],
  },
];

export const testimonials = [
  {
    id: 1,
    name: "Sarah Mitchell",
    role: "CEO, TechVault",
    content:
      "Your IT Solution transformed our online presence completely. Their web development team built an e-commerce platform that increased our sales by 150%. Highly recommend their expertise and professionalism.",
    rating: 5,
  },
  {
    id: 2,
    name: "James Rodriguez",
    role: "Marketing Director, GreenLife",
    content:
      "The digital marketing campaigns by Your IT Solution delivered a 300% ROI. Their data-driven approach and transparent reporting make them a fantastic partner for any business looking to grow.",
    rating: 5,
  },
  {
    id: 3,
    name: "Emily Chen",
    role: "Founder, FitPro Gym",
    content:
      "Our social media went from zero to hero with Your IT Solution. They understood our brand voice perfectly and built an engaged community that drives real business results.",
    rating: 5,
  },
  {
    id: 4,
    name: "Michael Brooks",
    role: "CTO, FinTrack Pro",
    content:
      "Working with Your IT Solution was seamless from start to finish. They delivered our SaaS dashboard ahead of schedule with exceptional quality. A truly world-class development team.",
    rating: 5,
  },
];

export const teamMembers = [
  {
    id: 1,
    name: "Alex Turner",
    role: "Founder & CEO",
    bio: "Visionary leader with 12+ years in digital strategy and business transformation.",
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
  },
  {
    id: 2,
    name: "Priya Sharma",
    role: "Head of Development",
    bio: "Full-stack architect passionate about building scalable, beautiful web applications.",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face",
  },
  {
    id: 3,
    name: "David Kim",
    role: "Digital Marketing Lead",
    bio: "Data-driven marketer with expertise in PPC, SEO, and growth strategy.",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
  },
  {
    id: 4,
    name: "Sophie Laurent",
    role: "Creative Director",
    bio: "Award-winning designer shaping unforgettable brand experiences across all platforms.",
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face",
  },
];

export const whyChooseUs = [
  {
    icon: Award,
    title: "Proven Results",
    description:
      "Every project is backed by measurable outcomes. We don't just deliver — we deliver growth.",
  },
  {
    icon: HeartHandshake,
    title: "Client-First Approach",
    description:
      "Your success is our priority. We listen, strategize, and execute with your goals in mind.",
  },
  {
    icon: Zap,
    title: "Cutting-Edge Tech",
    description:
      "We use the latest tools and frameworks to build solutions that are fast, scalable, and future-proof.",
  },
  {
    icon: Shield,
    title: "Transparent Process",
    description:
      "Clear communication, detailed reporting, and no hidden costs. You're always in the loop.",
  },
  {
    icon: Clock,
    title: "On-Time Delivery",
    description:
      "We respect deadlines and deliver projects on schedule without compromising quality.",
  },
  {
    icon: Headphones,
    title: "Dedicated Support",
    description:
      "Our team is available whenever you need us — from project kickoff to post-launch support.",
  },
];

export const companyStats = [
  { value: "150+", label: "Projects Delivered" },
  { value: "50+", label: "Happy Clients" },
  { value: "5+", label: "Years Experience" },
  { value: "98%", label: "Client Retention" },
];

export const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Contact", path: "/contact" },
];

export const footerLinks = {
  services: [
    { name: "Web Development", path: "/services" },
    { name: "Digital Marketing", path: "/services" },
    { name: "Social Media", path: "/services" },
    { name: "Video Editing", path: "/services" },
    { name: "Graphics Design", path: "/services" },
    { name: "Content Writing", path: "/services" },
  ],
  company: [
    { name: "About Us", path: "/about" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "Contact", path: "/contact" },
  ],
  contact: {
    email: "info.youritsolution@gmail.com",
    phone: "+8801338079054",
    whatsapp: "8801338079054",
    address: "Dhaka, Dhaka Division, Bangladesh",
  },
  social: {
    facebook: "https://facebook.com/youritsolutionn",
    instagram: "https://instagram.com/youritsolutionn",
    tiktok: "https://www.tiktok.com/@your_it_solution",
    linkedin: "https://linkedin.com/company/youritsolutionn",
    Youtube: "https://www.youtube.com/@YourITSolutionn"
  },
};
